import { useEffect, type FC } from 'react';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import {
  Box,
  ButtonLink,
  Column,
  Columns,
  Hidden,
  IconClear,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { SeekRecruiterNetworkLogo } from './SeekRecruiterNetworkLogo';
import { NewBadge } from 'src/components/common/NewBadge';
import { add, isBefore, parseISO, isAfter } from 'date-fns';
import {
  trackRecruiterNetworkBannerButtonPressedEvent,
  trackRecruiterNetworkBannerDisplay,
  trackRecruiterNetworkBannerDismissEvent,
} from 'src/utils/events';
import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';

const allowedRecruiterNetworkClassifications: string[] = [
  '1200', // Accounting
  '6251', // Administration & Office Support
  '1206', // Construction
  '1209', // Engineering
  '6281', // Information & Communication Technology
  '1216', // Legal
  '6092', // Manufacturing, Transport & Logistics
  '6008', // Marketing & Communications
  '1220', // Real Estate & Property
  '6362', // Sales
];

const allowedRecruiterNetworkLocations: Record<string, boolean> = {
  '1000': true,
  '1002': true,
  '1004': true,
  '1007': true,
  '1009': true,
};

const shouldShowRecruiterNetwork = (
  jobCreatedDate: string,
  classificationId: string | null | undefined,
  locationIds: string[],
): boolean => {
  const isoDate = parseISO(jobCreatedDate);

  return Boolean(
    isBefore(isoDate, add(Date.now(), { days: -12 })) &&
      isAfter(isoDate, add(Date.now(), { months: -6 })) &&
      classificationId &&
      allowedRecruiterNetworkClassifications.includes(classificationId) &&
      locationIds &&
      locationIds.filter(
        (locationId) => allowedRecruiterNetworkLocations[locationId],
      ).length !== 0,
  );
};

interface RecruiterNetworkAlertProps {
  hideAlert: () => void;
  jobCreatedDate: string;
  categoryId: string | null | undefined;
  locationIds: string[];
}

interface SRNAlertProps {
  hideAlert: () => void;
}

export const RecruiterNetworkAlert: FC<RecruiterNetworkAlertProps> = ({
  hideAlert,
  jobCreatedDate,
  categoryId,
  locationIds,
}) => {
  const showRecruiterNetwork = shouldShowRecruiterNetwork(
    jobCreatedDate,
    categoryId,
    locationIds,
  );
  if (!showRecruiterNetwork) {
    return null;
  }

  return <SRNAlert hideAlert={hideAlert} />;
};

const SRNAlert: FC<SRNAlertProps> = ({ hideAlert }) => {
  useEffect(() => {
    trackRecruiterNetworkBannerDisplay({
      currentPage: TRACKING_CANDIDATE_LIST,
    });
  }, []);

  const { t } = useTranslations(translations);

  return (
    <Box marginY="medium">
      <Box
        style={{ backgroundColor: '#E6EAFC' }}
        borderRadius="xlarge"
        padding={{
          desktop: 'large',
          tablet: 'large',
          mobile: 'medium',
        }}
      >
        {/* this is silly, but reverse doesn't count for collapsing, so to get the CloseIcon to render at the top when they collapse im just rendering them reverse when theyre not collapsed. confusing */}
        <Columns space="medium" collapseBelow="tablet" reverse={true}>
          <Column width="content">
            <Box
              display="flex"
              alignItems="center"
              justifyContent={{
                desktop: 'center',
                tablet: 'center',
                mobile: 'flexEnd',
              }}
            >
              <Box
                component="button"
                aria-label={t('Close')}
                borderRadius="full"
                cursor="pointer"
                position="relative"
                onClick={() => {
                  trackRecruiterNetworkBannerDismissEvent({});
                  hideAlert();
                }}
                outline="none"
                transition="touchable"
                transform={{ active: 'touchable' }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  component="span"
                  display="block"
                  zIndex={1}
                  position="relative"
                >
                  <IconClear size="fill" tone="neutral" />
                </Box>
              </Box>
            </Box>
          </Column>

          <Column>
            <Box
              display="flex"
              width="full"
              justifyContent="spaceBetween"
              height="full"
            >
              <Stack space="large">
                <Inline space="small" alignY="center">
                  <SeekRecruiterNetworkLogo />
                  <Box style={{ paddingBottom: '5px' }}>
                    <NewBadge weight="strong" />
                  </Box>
                </Inline>

                <Stack space="medium">
                  <Text size="large" weight="strong">
                    {t('Reduce how much vacancies are costing your business')}
                  </Text>

                  <Text>
                    {t(
                      'Connect with experienced recruiters who specialise in filling roles like yours',
                    )}
                    <br />
                    {t('Find out how SEEK Recruiter Network can work for you.')}
                  </Text>
                </Stack>

                <Inline space="small">
                  <ButtonLink
                    href="https://talent.seek.com.au/recruiter-network"
                    target="_blank"
                    onClick={() =>
                      trackRecruiterNetworkBannerButtonPressedEvent({
                        bannerElement: t('Go to SEEK Recruiter Network'),
                      })
                    }
                    role="button"
                  >
                    {t('Go to SEEK Recruiter Network')}
                  </ButtonLink>
                </Inline>
              </Stack>

              <Hidden below="desktop">
                <Box
                  alignItems="center"
                  display="flex"
                  flexGrow={1}
                  flexShrink={0}
                  width="full"
                  paddingLeft="large"
                  height="full"
                >
                  <Box
                    component="img"
                    role="img"
                    src="https://seekcdn.com/cm-ui/images/CandiMan-SRN.png"
                    style={{ height: 'auto', margin: 'auto', width: '248px' }}
                    alignItems="center"
                    display="flex"
                    flexGrow={1}
                  />
                </Box>
              </Hidden>
            </Box>
          </Column>
        </Columns>
      </Box>
    </Box>
  );
};
